import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CourierIncidentOpsView, CourierIncidentResolutionView } from '@vello/api-client';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, ListTable, Notice, Panel, Select, type Column, SectionTitle, Text, Row, Stack, Ink, useAutoRefresh } from '@vello/web-theme';
import { COURIER_INCIDENT_RESOLUTIONS, courierIncidentAuthority, formatMarketInstant, shortId, sortIncidents } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { CentralLinkBadge, useCentralLinks } from './central-link';

export function CourierIncidentsPanel({ market, timeZone }: { readonly market: string; readonly timeZone: string | undefined }): ReactElement | null {
  const { runtime, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = courierIncidentAuthority(grants);
  const [rows, setRows] = useState<readonly CourierIncidentOpsView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [choice, setChoice] = useState<Readonly<Record<string, CourierIncidentResolutionView>>>({});
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const refuse = useCallback((reasonCode: string | undefined, fallback: string, status?: number): string => {
    const text = refusalOf('refusal.admin.courier_incident', { code: fallback, reasonCode, status });
    return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
  }, [t, fail]);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    const res = await runtime.centralDesk.courierIncidents(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setRows(res.value.incidents);
      setError(undefined);
    } else setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
  }, [runtime, market, refuse]);

  useEffect(() => {
    if (!authority.mayView) return;
    setRows(undefined);
    void load();
  }, [authority.mayView, load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: authority.mayView });

  if (!authority.mayView) return null;

  const resolve = async (incident: CourierIncidentOpsView): Promise<void> => {
    const resolution = choice[incident.incidentId] ?? 'resumed';
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.centralDesk.resolveCourierIncident(incident.incidentId, market, resolution);
    setBusy(false);
    if (res.ok) setNotice({ tone: 'success', text: t('cdesk.inc.done') });
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const centralLinks = useCentralLinks('courier_incident', (rows ?? []).map((i) => i.incidentId));
  const columns: readonly Column<CourierIncidentOpsView>[] = [
    { key: 'when', header: t('cdesk.inc.col.when'), width: 'minmax(150px, 1fr)', render: (i) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(i.createdAt, timeZone, lang, 'datetime')}</Text> },
    { key: 'code', header: t('cdesk.inc.col.report'), width: 'minmax(180px, 1.2fr)', render: (i) => <Badge tone={i.status === 'OPEN' ? 'warning' : 'neutral'}>{v('central_desk.courier_incident_code', i.code)}</Badge> },
    { key: 'job', header: t('cdesk.inc.col.job'), width: 'minmax(120px, 0.8fr)', render: (i) => <span className="tnum">{i.orderCode ?? shortId(i.jobId)}</span> },
    { key: 'stage', header: t('cdesk.inc.col.stage'), width: 'minmax(140px, 1fr)', render: (i) => <Text role="caption" tone="secondary">{v('central_desk.courier_incident_stage', i.stage)}</Text> },
    { key: 'note', header: t('cdesk.inc.col.note'), width: 'minmax(200px, 2fr)', render: (i) => <Ink breakAnywhere>{i.note ?? '—'}</Ink> },
    // D637: the courier's report is a case of the courier's Atendimento in the Central.
    { key: 'central', header: t('central.link.col'), width: 'minmax(160px, 1fr)', render: (i) => <CentralLinkBadge link={centralLinks.get(i.incidentId)} /> },
    {
      key: 'act',
      header: '',
      width: 'minmax(260px, 1.6fr)',
      render: (i) =>
        i.status === 'RESOLVED' ? (
          <Badge tone="neutral">{v('central_desk.courier_incident_resolution', i.resolution ?? 'dismissed')}</Badge>
        ) : authority.mayResolve ? (
          <Row gap={2} align="center" wrap>
            <Select aria-label={t('cdesk.inc.resolutionLabel')} value={choice[i.incidentId] ?? 'resumed'} onChange={(e) => setChoice({ ...choice, [i.incidentId]: e.target.value as CourierIncidentResolutionView })}>
              {COURIER_INCIDENT_RESOLUTIONS.map((r) => (
                <option key={r} value={r}>{v('central_desk.courier_incident_resolution', r)}</option>
              ))}
            </Select>
            <Button variant="secondary" disabled={busy} onClick={() => void resolve(i)}>{t('cdesk.inc.close')}</Button>
          </Row>
        ) : (
          <Badge tone="warning">{t('cdesk.inc.open')}</Badge>
        ),
    },
  ];

  return (
    <Panel><Stack gap={4}>
      <SectionTitle>{t('cdesk.inc.title')}</SectionTitle>
      <Text as="p" role="caption" tone="secondary">{t('cdesk.inc.hint')}</Text>
      {error !== undefined && <div><Notice tone="danger">{error}</Notice></div>}
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}
      <ListTable
        columns={columns}
        rows={sortIncidents(rows ?? [])}
        rowKey={(i) => i.incidentId}
        loading={rows === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('cdesk.inc.failed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('cdesk.inc.empty')} />}
        minWidth={980}
      />
    </Stack></Panel>
  );
}
